import React from 'react';
import { Pressable, TextInput as RNTextInput } from 'react-native';
import { Text } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { COLORS } from '@/constants/colors';
import { PRESET_AMOUNTS } from '@/data/walletData';
import {
  getCardBackgroundColor,
  getCardBorderColor,
  getInputBackgroundColor,
  getInputBorderColor,
  BORDER_RADIUS_MEDIUM,
  BORDER_RADIUS_LARGE,
  CARD_BORDER_RADIUS,
  CARD_PADDING,
  INPUT_PADDING,
  INPUT_FONT_SIZE,
  FONT_SIZE_LG,
  FONT_SIZE_2XL,
  FONT_SIZE_XS,
  FONT_SIZE_BASE,
  BORDER_WIDTH_THIN,
} from '../constants';

export type AmountSelectorProps = {
  isDark: boolean;
  textColor: string;
  subtextColor: string;
  selectedAmount: number | null;
  customAmount: string;
  onAmountSelect: (amount: number) => void;
  onCustomAmountFocus: () => void;
  onCustomAmountChange: (text: string) => void;
};

const AmountSelector: React.FC<AmountSelectorProps> = React.memo(({
  isDark,
  textColor,
  subtextColor,
  selectedAmount,
  customAmount,
  onAmountSelect,
  onCustomAmountFocus,
  onCustomAmountChange,
}) => {
  return (
    <YStack
      backgroundColor={getCardBackgroundColor(isDark)}
      borderRadius={CARD_BORDER_RADIUS}
      padding={CARD_PADDING}
      borderWidth={BORDER_WIDTH_THIN}
      borderColor={getCardBorderColor(isDark)}
      space="$3"
    >
      <Text fontSize={FONT_SIZE_LG} fontWeight="700" color={textColor}>
        Wybierz kwotę
      </Text>
      
      {/* Preset amounts */}
      <XStack gap="$3" flexWrap="wrap">
        {PRESET_AMOUNTS.map((item) => (
          <Pressable
            key={item.amount}
            onPress={() => onAmountSelect(item.amount)}
            style={{ flex: 1, minWidth: '45%' }}
          >
            <YStack
              backgroundColor={
                selectedAmount === item.amount
                  ? COLORS.PRIMARY
                  : getInputBackgroundColor(isDark)
              }
              paddingVertical="$3"
              paddingHorizontal="$2"
              borderRadius={BORDER_RADIUS_MEDIUM}
              alignItems="center"
              borderWidth={BORDER_WIDTH_THIN}
              borderColor={
                selectedAmount === item.amount
                  ? COLORS.PRIMARY
                  : getInputBorderColor(isDark)
              }
              space="$1"
            >
              <Text
                fontSize={FONT_SIZE_2XL}
                fontWeight="700"
                color={selectedAmount === item.amount ? COLORS.WHITE : textColor}
              >
                {item.amount} zł
              </Text>
              <Text
                fontSize={FONT_SIZE_XS}
                fontWeight="500"
                color={selectedAmount === item.amount ? COLORS.WHITE : subtextColor}
              >
                {item.label}
              </Text>
            </YStack>
          </Pressable>
        ))}
      </XStack>

      {/* Custom amount */}
      <YStack space="$2">
        <RNTextInput
          value={customAmount}
          onChangeText={onCustomAmountChange}
          onFocus={onCustomAmountFocus}
          placeholder="Wpisz własną kwotę..."
          placeholderTextColor={subtextColor}
          keyboardType="decimal-pad"
          style={{
            backgroundColor: getInputBackgroundColor(isDark),
            borderRadius: BORDER_RADIUS_MEDIUM,
            padding: INPUT_PADDING,
            fontSize: INPUT_FONT_SIZE,
            fontWeight: '500',
            color: textColor,
            borderWidth: BORDER_WIDTH_THIN,
            borderColor: getInputBorderColor(isDark, !!customAmount),
          }}
        />
      </YStack>
    </YStack>
  );
});

AmountSelector.displayName = 'AmountSelector';

export default AmountSelector;

